import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Diamond, Gift, ChevronLeft, ChevronRight, Eye } from 'lucide-react';

interface Produto {
  id: string;
  nome: string;
  pontos: number;
  descricao: string;
  imagem: string;
  disponivel: number;
}

const Diamantes = () => {
  const [saldoDiamantes] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const produtos: Produto[] = [
    {
      id: '1',
      nome: 'Cafeteira Elétrica Philco Preta - 110V',
      pontos: 188,
      descricao: 'Cafeteira Philco 20 cafés com base aquecida, corta-pingos, jarra de vidro e filtro permanente. (prazo de chegada no estoque: 5 a 15 dias)',
      imagem: '/placeholder-coffee.jpg',
      disponivel: 3
    },
    {
      id: '2',
      nome: 'Secador de Cabelo Philco Íons Tourmaline Preto - Bivolt',
      pontos: 218,
      descricao: 'Secador Philco 2200W com íons Tourmaline, 3 temperaturas, jato frio, bocal difusor e concentrador. (prazo de chegada no estoque: 5 a 15 dias)',
      imagem: '/placeholder-dryer.jpg',
      disponivel: 3
    },
    {
      id: '3',
      nome: 'Mini Secador de Cabelo Mondial Tourmaline Rose - Bivolt',
      pontos: 118,
      descricao: 'Secador Mondial com Tourmaline Ion, bivolt e cabo dobrável. Compacto e ideal para viagens. (prazo de chegada no estoque: 5 a 15 dias)',
      imagem: '/placeholder-mini-dryer.jpg',
      disponivel: 2
    }
  ];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % produtos.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + produtos.length) % produtos.length);
  };

  return (
    <div className="p-4 space-y-6">
      {/* Header com saldo */}
      <Card className="bg-gradient-to-r from-purple-light to-blue-50 border-purple/20">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <Diamond className="w-8 h-8 text-purple" />
          </div>
          <CardTitle className="text-lg">Programa de Benefícios</CardTitle>
          <div className="flex items-center justify-center gap-2">
            <span className="text-sm text-muted-foreground">Saldo disponível:</span>
            <Badge variant="outline" className="text-lg font-bold px-3 py-1">
              {saldoDiamantes} 💎
            </Badge>
            <Button variant="ghost" size="sm" className="text-blue-500">
              <Eye className="w-4 h-4 mr-1" />
              Ver extratos
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Seção de benefícios */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Benefícios para resgatar</h2>
        </div>

        {/* Carousel de produtos */}
        <div className="relative">
          <div className="overflow-hidden rounded-xl">
            <div 
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {produtos.map((produto) => (
                <div key={produto.id} className="w-full flex-shrink-0">
                  <Card className="mx-2">
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        {/* Header do produto */}
                        <div className="flex justify-between items-start">
                          <div className="bg-gold-light text-gold px-3 py-1 rounded-full text-sm font-semibold">
                            {produto.pontos} 💎
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {produto.disponivel} unid. disponíveis
                          </div>
                        </div>

                        {/* Nome do produto */}
                        <h3 className="font-semibold text-sm leading-tight text-center">
                          {produto.nome}
                        </h3>

                        {/* Imagem placeholder */}
                        <div className="aspect-square bg-muted rounded-lg flex items-center justify-center">
                          <Gift className="w-16 h-16 text-muted-foreground" />
                        </div>

                        {/* Descrição */}
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {produto.descricao}
                        </p>

                        {/* Botão de resgate */}
                        <Button 
                          className="w-full bg-success hover:bg-success/90"
                          disabled={saldoDiamantes < produto.pontos}
                        >
                          Resgatar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {/* Controles do carousel */}
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Indicadores */}
          <div className="flex justify-center gap-2 mt-4">
            {produtos.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Info card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-sm text-blue-700">
            💡 <strong>Dica:</strong> Para gerar recibos e garantias de vendas realizadas, acesse o menu{' '}
            <span className="font-semibold">Minhas Vendas</span>!
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Diamantes;